'use client';

import { Radio, Stack, Typography } from '@mui/material';
import type { InventoryResource, Snapshot } from '@repo/api-gw-sdk';
import classNames from 'classnames';

import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { AccountAsVaultTag } from '@/components/vaults/vaultTag';
import type { BackupVault } from '@/data/vaults/backupVault';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, TIME_FORMAT } from '@/utils/dayjs';

import type { MutateSnapshots } from './eonSnapshotsTab';
import { SnapshotActionsGroup } from './snapshotActionsGroup';
import { SnapshotDetailsHeader } from './snapshotDetailsHeader';

interface RedshiftSnapshotDetailsProps {
  snap: Snapshot;
  vault: BackupVault | undefined;
  isSelected: boolean;
  onSelect: () => void;
  onRestore: () => void;
  resource: InventoryResource;
  snapshotSelection: boolean;
  mutateSnapshots: MutateSnapshots;
}

export const RedshiftSnapshotDetails = ({
  snap,
  vault,
  isSelected,
  onSelect,
  onRestore,
  resource,
  snapshotSelection,
  mutateSnapshots,
}: RedshiftSnapshotDetailsProps) => {
  const isNativeSnapshot = snap.isNative ?? false;

  return (
    <Stack
      key={snap.id}
      gap='12px'
      data-testid='redshift-snapshot-row'
      borderBottom='1px solid var(--mui-palette-divider)'
      className='py-[24px] px-[40px] w-full'
      direction='row'
      justifyContent='space-between'
      alignItems='center'
    >
      {isNativeSnapshot ? (
        <RedshiftNativeSnapshotHeader
          snap={snap}
          resource={resource}
          isSelected={isSelected}
          onSelect={onSelect}
          snapshotSelection={snapshotSelection}
        />
      ) : (
        <SnapshotDetailsHeader
          snap={snap}
          vault={vault}
          isSelected={isSelected}
          onSelect={onSelect}
          snapshotSelection={snapshotSelection}
        />
      )}
      <SnapshotActionsGroup
        snap={snap}
        isVisible={!snapshotSelection || isSelected}
        onRestore={onRestore}
        resource={resource}
        mutateSnapshots={mutateSnapshots}
      />
    </Stack>
  );
};

const RedshiftNativeSnapshotHeader = ({
  snap,
  resource,
  isSelected,
  onSelect,
  snapshotSelection,
}: {
  snap: Snapshot;
  resource: InventoryResource;
  isSelected: boolean;
  onSelect: () => void;
  snapshotSelection: boolean;
}) => {
  const date = isDateValid(snap.pointInTime)
    ? snap.pointInTime
    : snap.createdTime;
  const redshiftProps = snap.redshiftSnapshotProperties;
  const region = redshiftProps?.region || resource.region;

  return (
    <Stack
      className={classNames('flex-1', { 'cursor-pointer': snapshotSelection })}
      direction='row'
      gap='40px'
      alignItems='center'
      onClick={() => onSelect()}
    >
      <Stack direction='row' gap='60px' alignItems='center'>
        <Stack width='238px' direction='row' gap='36px' alignItems='center'>
          <Stack direction='row' gap='12px' alignItems='center'>
            {snapshotSelection && <Radio size='small' checked={isSelected} />}
            <SnapshotIndicator
              sx={{ margin: '6px' }}
              backgroundColor={null}
              isNative
            />
            <Typography width='100px' variant='subtitle1'>
              {`${dayjs.utc(date).format(TIME_FORMAT)} UTC`}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' gap='12px' alignItems='center'>
          <Typography variant='subtitle1'>Stored in</Typography>
          <AccountAsVaultTag
            cloudProvider={resource.cloudProvider}
            region={region}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
